import type { AuthedAxiosRequests } from 'features/auth'
import {
  type DocumentPageMetadataUserEditEntry,
  parseDocumentPageMetadataUserEditEntries,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  parseSystemDocumentPageEntries,
  type SystemDocumentPageEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import type { MetadataUserEditLoadOptions } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { postFetcher } from 'features/rolodex/fetchers'
import { z } from 'zod'

const EntryEnvelopeSchema = z.looseObject({
  entry: z.looseObject({ id: z.string() }),
})

type RevisionFamilyLoadArgs = {
  axios: AuthedAxiosRequests
  rolodexUrl: string
  loadOptions: MetadataUserEditLoadOptions
}

async function loadRevisionFamilyEntries({
  axios,
  rolodexUrl,
  loadOptions,
}: RevisionFamilyLoadArgs): Promise<z.infer<typeof EntryEnvelopeSchema>[]> {
  const endpoint = `${rolodexUrl}/api/v2/entries/load`
  const options = {
    ...loadOptions,
    limit: Math.min(loadOptions.limit, 1000),
    order_by: [{ field: 'id', descending: false }],
    offset: 0,
  }
  const rawCount: unknown = await postFetcher(
    `${endpoint}/count`,
    JSON.stringify(options),
    axios
  )
  const count = z.number().int().nonnegative().parse(rawCount)
  const entries = new Map<string, z.infer<typeof EntryEnvelopeSchema>>()
  let offset = 0
  while (entries.size < count) {
    const raw: unknown = await postFetcher(
      endpoint,
      JSON.stringify({ ...options, offset }),
      axios
    )
    const rows = z.array(EntryEnvelopeSchema).parse(raw)
    const before = entries.size
    for (const edit of rows) entries.set(edit.entry.id, edit)
    offset += rows.length
    if (entries.size === before || rows.length < options.limit) break
  }
  if (entries.size < count) {
    throw new Error('Revision family entry load is incomplete')
  }
  return [...entries.values()]
}

export async function loadPageRevisionFamilyEdits(
  args: RevisionFamilyLoadArgs
): Promise<DocumentPageMetadataUserEditEntry[]> {
  return parseDocumentPageMetadataUserEditEntries(
    await loadRevisionFamilyEntries(args)
  )
}

export async function loadPageRevisionFamilyPages(
  args: RevisionFamilyLoadArgs
): Promise<SystemDocumentPageEntry[]> {
  return parseSystemDocumentPageEntries(await loadRevisionFamilyEntries(args))
}
